import { saveAuthenticationData, useDomain, useLogin } from '@netfront/gelada-identity-library';
import { useRouter } from 'next/router';
import { object, string } from 'yup';
import { Button, Dialog, Input, PasswordInput, useToast } from '@netfront/ui-library';
import { ILoginFormFields } from './LoginForm.interfaces';
import Link from 'next/link';
import { ICookieOptionalAttributes, useToggle } from '@netfront/common-library';
import { useForm, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { environmentVariables } from '@/utils/environmentVariables';
import { ApolloError, ServerError } from '@apollo/client';
import EnterTwoFactorCodeInput from '../ManageTwoFactorAuthenticationForm/Components/EnterTwoFactorCodeInput/EnterTwoFactorInput';
import { useId, useState } from 'react';

const MFA_CODE_REQUESTED = 'MFA_CODE_REQUESTED';

// Only follow the `redirect` query param when it is a same-site relative path, to avoid an open redirect.
function getSafeRedirectPath(redirect: string | string[] | undefined): string {
  if (typeof redirect !== 'string' || !redirect.startsWith('/') || redirect.startsWith('//')) {
    return '/console';
  }

  return redirect;
}

export const LoginForm = () => {
  const {
    push,
    query: { redirect },
  } = useRouter();
  const { handleToastError } = useToast();
  const { getDomain } = useDomain();
  const { isToggled: isLoggingIn, toggle: toggleIsLoggingIn } = useToggle();

  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isLoading },
  } = useForm<ILoginFormFields>({
    resolver: yupResolver(
      object({
        email: string().email('Please enter a valid email address').required('Please enter a valid email address'),
        password: string().required('Please enter a password'),
        mfaCode: string(),
      }),
    ) as Resolver<ILoginFormFields>,
    mode: 'onBlur',
  });

  const { handleLogin } = useLogin({
    onCompleted: ({ accessToken, refreshToken, user }) => {
      if (!user) return;

      const domain = getDomain();
      const isSecure = process.env.REACT_APP_COOKIE_ATTRIBUTE_SECURE === 'true';
      const sameSite: ICookieOptionalAttributes['sameSite'] = isSecure ? 'None' : 'Lax';

      saveAuthenticationData({
        accessToken,
        accessTokenOptionalCookieAttributes: {
          domain,
          sameSite,
          secure: isSecure,
        },
        refreshToken,
        refreshTokenOptionalCookieAttributes: {
          domain,
          sameSite,
          secure: isSecure,
        },
        user,
        userOptionalCookieAttributes: {
          domain,
          sameSite,
          secure: isSecure,
        },
      });
 
      push(getSafeRedirectPath(redirect));
    },
    onError: (error) => {
      toggleIsLoggingIn();

      const mappedError = error as ApolloError & {
        networkError: ServerError & {
          result?: {
            errors?: {
              message: string;
              extensions: {
                code: string;
                codes: string[];
              };
            }[];
          };
        };
      };
      const apiError = mappedError.networkError?.result.errors?.at(0);
      if (apiError && apiError.extensions.code === MFA_CODE_REQUESTED) {
        setIsConfirmationModalVisible(true);
        return;
      }

      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  const onSubmit = handleSubmit(({ email, password, mfaCode }: ILoginFormFields) => {
    toggleIsLoggingIn();
    handleLogin({
      projectId: environmentVariables.projectId,
      login: email,
      password: password,
      mfaCode,
    });
    setIsConfirmationModalVisible(false);
  });

  return (
    <>
      <Dialog title="Enter your MFA code" isOpen={isConfirmationModalVisible} onClose={() => setIsConfirmationModalVisible(false)}>
        <EnterTwoFactorCodeInput
          id={useId()}
          isSubmitting={isLoading}
          onEnterTwoFactorCode={(code) => {
            setValue('mfaCode', code);
            onSubmit();
          }}
        />
      </Dialog>
      <form onSubmit={onSubmit}>
        <Input
          autoComplete="email"
          errorMessage={errors.email?.message || ''}
          id="email"
          inputMode="email"
          labelText="Email"
          {...register('email')}
          type="email"
          isRequired
        />

        <PasswordInput
          errorMessage={errors.password?.message || ''}
          id="password"
          labelText="Password"
          {...register('password')}
          isRequired
        />

        <Button text="Sign in" type="submit" isLoading={isLoggingIn} />
      </form>

      <div className='mt-4'>
        <p>
          Don't have an account? <Link href="/register">Register</Link>
        </p>
        <Link href="/forgot-password">Forgot password</Link>
      </div>
    </>
  );
};
